import Swiper from 'swiper';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import Plyr from 'plyr';
import TermsModal from '../downloads/terms-modal';

let downloadsWrapper: HTMLDivElement;
let downloadsFilterForm: HTMLFormElement;
let footer: HTMLElement;
let termsModal: TermsModal;
let termsOfUseText: string | null = null;

// Keep references to Plyr instances to control them (e.g. pause on slide changes)
const plyrInstances: Plyr[] = [];

const buildSearchStr = (): void => {
  const formData = new FormData(downloadsFilterForm);
  const queryUrl = new URLSearchParams();

  for (const [key, val] of formData.entries()) {
    if (val && val.toString() !== '-') {
      queryUrl.append(key, val.toString());
    }
  }

  window.location.href =
    queryUrl.toString() === ''
      ? downloadsFilterForm.action
      : `${downloadsFilterForm.action}?${queryUrl.toString()}`;
};

const handleSubmit = (e: Event): void => {
  e.preventDefault();
  buildSearchStr();
};

const checkBtnClick = (target: HTMLElement) => {
  const availableClasses = [
    'downloads__filter-header-close',
    'downloads__filter-fixed-btn',
    'downloads__filter-bg',
  ];

  if (availableClasses.some(item => target.classList.contains(item))) {
    downloadsWrapper.classList.toggle('downloads_open');
  }
};

const handleDownloadClick = (e: MouseEvent): void => {
  const target = e.target as HTMLElement;
  const downloadBtn = target.closest('[data-download-btn]') as HTMLButtonElement;

  if (!downloadBtn) {
    return;
  }

  const downloadUrl = downloadBtn.dataset.downloadUrl;

  if (!downloadUrl) {
    return;
  }

  // Check if terms of use text is present and not accepted
  if (termsOfUseText && !termsModal.hasAcceptedTerms()) {
    e.preventDefault();
    e.stopPropagation();

    termsModal.show({
      termsText: termsOfUseText,
      onAccept: () => {
        // Trigger download after accepting
        const tempLink = document.createElement('a');
        tempLink.href = downloadUrl;
        tempLink.download = '';
        document.body.appendChild(tempLink);
        tempLink.click();
        document.body.removeChild(tempLink);
      },
      onDecline: () => {
        // Do nothing, download is cancelled
      },
    });
  } else {
    // Directly download if terms are already accepted or not required
    const tempLink = document.createElement('a');
    tempLink.href = downloadUrl;
    tempLink.download = '';
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  }
};

const handleClick = (e: MouseEvent): void => {
  const target = e.target as HTMLElement;

  // Handle download button clicks
  if (target.closest('[data-download-btn]')) {
    handleDownloadClick(e);
    return;
  }

  const isToggleTarget = target.dataset.toggle !== undefined;
  const isDropdownTarget = target.classList.contains('downloads__dd-item');

  // Clear all visible dropdowns
  const visibleDD = downloadsFilterForm?.querySelectorAll(
    '.downloads__dd_focus',
  );

  if (visibleDD && visibleDD.length) {
    visibleDD.forEach(item => {
      if (
        !(isToggleTarget && target.closest('.downloads__dd')?.isSameNode(item))
      ) {
        item.classList.remove('downloads__dd_focus');
      }
    });
  }

  if (isDropdownTarget) {
    let key = target.dataset.key;
    let value = target.dataset.val;
    let label = target.dataset.label;

    if (key) {
      const input = downloadsFilterForm.querySelector(
        `[name="${key}"]`,
      ) as HTMLInputElement;
      if (input) {
        input.value = value;
      }

      const wrapper = document.querySelector(`[data-input-wrapper="${key}"]`);
      if (wrapper) {
        wrapper.querySelector('.downloads__dd-current-text').innerHTML = label;
        if (value.length > 0) {
          wrapper.classList.add('downloads__dd_active');
        } else {
          wrapper.classList.remove('downloads__dd_active');
        }
      }
    }
  }

  if (isToggleTarget) {
    target.closest('.downloads__dd')?.classList.toggle('downloads__dd_focus');
  }

  checkBtnClick(target);
};

const handleChange = (e: Event): void => {
  const target = e.target as HTMLSelectElement;
  if ('select-one' === target.type) {
    if (target.name) {
      const wrapper = document.querySelector(
        `[data-input-wrapper="${target.name}"]`,
      );
      if (wrapper) {
        if (target.value.length > 0) {
          wrapper.classList.add('downloads__dd_active');
        } else {
          wrapper.classList.remove('downloads__dd_active');
        }
      }
    }
    const ddCurrentText = target.parentElement.querySelector('.downloads__dd-current');
    ddCurrentText.innerHTML = target.options[target.selectedIndex].text;
  }
};

const handleMobileShadows = () => {
  if (downloadsWrapper && footer && window.innerWidth < 768) {
    const offset = footer.getBoundingClientRect().top - window.innerHeight;
    downloadsWrapper.classList.toggle('downloads_hide-shadow', offset <= 0);
  }
};

const events = (): void => {
  if (downloadsFilterForm) {
    downloadsFilterForm.addEventListener('submit', handleSubmit);
    downloadsFilterForm.addEventListener('change', handleChange);
  }

  if (downloadsWrapper) {
    downloadsWrapper.addEventListener('click', handleClick);
  }

  document.addEventListener('scroll', handleMobileShadows);
};

const pauseAllPlyr = (): void => {
  plyrInstances.forEach(p => {
    try {
      p.pause();
    } catch (e) {
      /* no-op */
    }
  });
};

const initPlyr = (): void => {
  // Select HTML5 videos and embedded iframes wrapped for Plyr
  const targets: NodeListOf<HTMLElement> = document.querySelectorAll(
    'video.plyr, [data-plyr-provider]',
  );

  targets.forEach((el: any) => {
    // Avoid double init; Plyr attaches a reference to the element
    const existing = el && (el as any).plyr;
    if (existing) {
      if (!plyrInstances.includes(existing)) {
        plyrInstances.push(existing);
      }
      return;
    }

    try {
      const player = new Plyr(el, {
        ratio: '1:1',
        youtube: { rel: 0, modestbranding: 1, playsinline: 1 },
        vimeo: { byline: false, portrait: false, title: false },
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'fullscreen',
        ],
      });
      plyrInstances.push(player);
    } catch (e) {
      // Safeguard: don't break the page if Plyr fails on an element
      // eslint-disable-next-line no-console
      console.warn('Plyr init failed', e);
    }
  });
};

const initSlider = (sliderClass: string) => {
  const swiper = new Swiper(sliderClass, {
    loop: true,
    autoplay: {
      delay: 3000,
      disableOnInteraction: true,
      pauseOnMouseEnter: true,
    },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
      type: 'bullets',
    },
    modules: [Pagination, Navigation, Autoplay],
  });

  // Pause playing videos when slide changes
  swiper.on('slideChange', () => {
    pauseAllPlyr();
  });
};

const init = (): void => {
  downloadsWrapper = document.querySelector('.social-media-library');
  footer = document.querySelector('footer');

  // Initialize terms modal
  termsModal = new TermsModal();

  // Get terms of use text from global variable
  termsOfUseText = (window as any).DENSO?.downloadsTermsOfUseText || null;

  if (downloadsWrapper) {
    downloadsFilterForm = downloadsWrapper.querySelector(
      '.downloads__filter-form',
    );

    events();
  }

  // Check if we're on the post detail page
  const postDetailPage = document.querySelector('.social-media-library-entry');
  if (postDetailPage) {
    // Initialize events for download button on post detail page
    document.addEventListener('click', handleClick);
  }

  let postImagesSlider = document.querySelector(
    '.post-images-slider-container',
  );
  if (postImagesSlider) {
    initSlider('.post-images-slider-container');
  }

  // Initialize Plyr players for any videos or embeds on the page
  initPlyr();
};

export default {
  init,
};
